import { useState, useMemo } from 'react';
import { categoriesAliments, Aliment } from '@/mocks/aliments';
import { useAliments } from '@/hooks/useAliments';

const categoryColors: Record<string, string> = {
  legumineuses: 'bg-lime-100 text-lime-700',
  feculents: 'bg-amber-100 text-amber-700',
  legumes: 'bg-emerald-100 text-emerald-700',
  fruits: 'bg-orange-100 text-orange-700',
  viandes: 'bg-rose-100 text-rose-700',
  poissons: 'bg-cyan-100 text-cyan-700',
  produits_laitiers: 'bg-sky-100 text-sky-700',
  boissons: 'bg-blue-100 text-blue-700',
  snacks: 'bg-yellow-100 text-yellow-700',
  desserts: 'bg-pink-100 text-pink-700',
  fast_food: 'bg-red-100 text-red-700',
  sauces: 'bg-stone-100 text-stone-700',
  petit_dejeuner: 'bg-amber-100 text-amber-800',
  produits_industriels: 'bg-violet-100 text-violet-700',
};

const igColor = (ig: number) => {
  if (ig === 0) return 'text-gray-400';
  if (ig < 40) return 'text-emerald-600';
  if (ig < 60) return 'text-amber-600';
  return 'text-red-600';
};

const igLabel = (ig: number) => {
  if (ig === 0) return 'N/A';
  if (ig < 40) return 'Bas';
  if (ig < 60) return 'Modéré';
  return 'Élevé';
};

const igBgColor = (ig: number) => {
  if (ig === 0) return 'bg-gray-50 text-gray-700';
  if (ig < 40) return 'bg-emerald-50 text-emerald-700';
  if (ig < 60) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-700';
};

const scoreConfianceStars = (score?: number) => {
  if (!score) return 'Non évalué';
  return '★'.repeat(score) + '☆'.repeat(5 - score);
};

function AlimentCard({ aliment, onClick }: { aliment: Aliment; onClick: () => void }) {
  const catLabel = categoriesAliments.find((c) => c.id === aliment.categorie)?.label ?? aliment.categorie;
  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-teal-200 transition-all duration-200 cursor-pointer"
    >
      <div className="relative h-40 overflow-hidden">
        <img src={aliment.image} alt={aliment.nom} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        <span className={`absolute top-3 left-3 text-xs font-semibold px-2 py-1 rounded-full ${categoryColors[aliment.categorie] ?? 'bg-gray-100 text-gray-600'}`}>
          {catLabel}
        </span>
        {aliment.scoreConfiance && (
          <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-1 rounded-full bg-white/90 text-amber-600 shadow-sm">
            {scoreConfianceStars(aliment.scoreConfiance)}
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-bold text-gray-900 text-sm mb-1 group-hover:text-teal-600 transition-colors">{aliment.nom}</h3>
        <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">{aliment.description}</p>
        <div className="flex items-center justify-between">
          <span className="bg-teal-50 text-teal-700 text-xs font-bold px-2.5 py-1 rounded-full">
            {aliment.glucidesPour100g}g G/100g
          </span>
          <span className={`text-xs font-semibold ${igColor(aliment.indexGlycemique)}`}>
            IG {igLabel(aliment.indexGlycemique)}
          </span>
        </div>
      </div>
    </div>
  );
}

function AlimentDetail({ aliment, onClose }: { aliment: Aliment; onClose: () => void }) {
  const catLabel = categoriesAliments.find((c) => c.id === aliment.categorie)?.label ?? aliment.categorie;

  const nutritionGrid = [
    { label: 'Glucides', value: `${aliment.glucidesPour100g}g`, sub: 'pour 100g', color: 'bg-teal-50 text-teal-700' },
    { label: 'Calories', value: `${aliment.calories100g}`, sub: 'kcal', color: 'bg-amber-50 text-amber-700' },
    { label: 'Fibres', value: `${aliment.fibres100g}g`, sub: 'pour 100g', color: 'bg-emerald-50 text-emerald-700' },
    { label: 'Index glycémique', value: aliment.indexGlycemique === 0 ? 'N/A' : `${aliment.indexGlycemique} (${igLabel(aliment.indexGlycemique)})`, sub: 'IG', color: igBgColor(aliment.indexGlycemique) },
    ...(aliment.proteines100g !== undefined ? [{ label: 'Protéines', value: `${aliment.proteines100g}g`, sub: 'pour 100g', color: 'bg-rose-50 text-rose-700' }] : []),
    ...(aliment.lipides100g !== undefined ? [{ label: 'Lipides', value: `${aliment.lipides100g}g`, sub: 'pour 100g', color: 'bg-yellow-50 text-yellow-700' }] : []),
    ...(aliment.sucres100g !== undefined ? [{ label: 'Sucres', value: `${aliment.sucres100g}g`, sub: 'dont sucres', color: 'bg-pink-50 text-pink-700' }] : []),
    ...(aliment.chargeGlycemique !== undefined ? [{ label: 'Charge glycémique', value: aliment.chargeGlycemique === 0 ? 'N/A' : String(aliment.chargeGlycemique), sub: 'par portion 100g', color: aliment.chargeGlycemique && aliment.chargeGlycemique > 10 ? 'bg-red-50 text-red-700' : aliment.chargeGlycemique && aliment.chargeGlycemique > 5 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700' }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-2xl overflow-hidden w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Image */}
        <div className="relative h-56 shrink-0">
          <img src={aliment.image} alt={aliment.nom} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="absolute bottom-4 left-5 right-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${categoryColors[aliment.categorie] ?? 'bg-gray-100 text-gray-600'}`}>
                {catLabel}
              </span>
              {aliment.scoreConfiance && (
                <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-white/90 text-amber-600 shadow-sm">
                  Fiabilité {scoreConfianceStars(aliment.scoreConfiance)}
                </span>
              )}
            </div>
            <h2 className="text-white font-bold text-2xl mt-2">{aliment.nom}</h2>
          </div>
        </div>

        <div className="p-6">
          {/* Description */}
          <p className="text-gray-600 text-sm leading-relaxed mb-6">{aliment.description}</p>

          {/* Source & Aliases */}
          {aliment.source && (
            <div className="flex items-start gap-3 mb-4 bg-gray-50 rounded-xl p-3">
              <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shrink-0">
                <i className="ri-book-line text-gray-500 text-sm"></i>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-700">Source des données</div>
                <div className="text-xs text-gray-500 mt-0.5">{aliment.source}</div>
              </div>
            </div>
          )}

          {aliment.aliases && aliment.aliases.length > 0 && (
            <div className="mb-5">
              <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Aussi connu sous</div>
              <div className="flex flex-wrap gap-1.5">
                {aliment.aliases.map((alias) => (
                  <span key={alias} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md">
                    {alias}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Nutrition Grid */}
          <div className="mb-2">
            <div className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">Valeurs nutritionnelles / 100g</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {nutritionGrid.map((item) => (
                <div key={item.label} className={`${item.color} rounded-xl p-3`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{item.label}</div>
                  <div className="font-bold text-base leading-tight">{item.value}</div>
                  <div className="text-[10px] opacity-60">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* IG / GL Info Card */}
          {(aliment.indexGlycemique > 0 || (aliment.chargeGlycemique && aliment.chargeGlycemique > 0)) && (
            <div className="mt-4 bg-teal-50 border border-teal-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-information-line text-teal-600 text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-teal-800">Impact glycémique</span>
              </div>
              <p className="text-teal-700 text-xs leading-relaxed">
                {aliment.chargeGlycemique && aliment.chargeGlycemique > 0
                  ? `Charge glycémique de ${aliment.chargeGlycemique} pour 100g — ${aliment.chargeGlycemique <= 10 ? 'faible' : aliment.chargeGlycemique <= 20 ? 'modérée' : 'élevée'}. `
                  : ''}
                {aliment.indexGlycemique > 0
                  ? `Index glycémique ${igLabel(aliment.indexGlycemique).toLowerCase()} (${aliment.indexGlycemique}). Associer avec des fibres, des protéines ou des lipides pour modérer la réponse glycémique.`
                  : 'Cet aliment ne contient pas de glucides et n\'a pas d\'impact sur la glycémie.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function InfoAlimentPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('tous');
  const [selectedAliment, setSelectedAliment] = useState<Aliment | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { allAliments: aliments, loading } = useAliments();

  // Recherche étendue : nom + aliases
  const matchesSearch = (a: Aliment, q: string) => {
    const term = q.toLowerCase();
    if (a.nom.toLowerCase().includes(term)) return true;
    if (a.aliases?.some((alias) => alias.toLowerCase().includes(term))) return true;
    return false;
  };

  const suggestions = useMemo(() => {
    if (!search.trim()) return [];
    return aliments.filter((a) => matchesSearch(a, search)).slice(0, 5);
  }, [search, aliments]);

  const filtered = useMemo(() => {
    let list = aliments;
    if (activeCategory !== 'tous') list = list.filter((a) => a.categorie === activeCategory);
    if (search.trim()) list = list.filter((a) => matchesSearch(a, search));
    return list;
  }, [search, activeCategory, aliments]);

  return (
    <>
      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Header */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-8">
              <span className="inline-block bg-teal-50 text-teal-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                Bibliothèque nutritionnelle
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Info Aliment</h1>
              <p className="text-gray-500 text-lg max-w-xl mx-auto">
                Recherchez un aliment et obtenez instantanément ses valeurs nutritionnelles détaillées, son index glycémique, sa charge glycémique et ses synonymes.
              </p>
            </div>

            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <div className="flex items-center gap-3 bg-white border-2 border-gray-200 focus-within:border-teal-400 rounded-full px-5 py-3 transition-colors">
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-search-line text-gray-400 text-lg"></i>
                </div>
                <input
                  type="text"
                  placeholder="Rechercher un aliment ou un synonyme..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="flex-1 outline-none text-gray-800 text-sm bg-transparent placeholder-gray-400"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>

              {/* Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl overflow-hidden z-20">
                  {suggestions.map((s) => (
                    <button
                      key={s.id}
                      onMouseDown={() => { setSelectedAliment(s); setSearch(s.nom); setShowSuggestions(false); }}
                      className="w-full flex items-center gap-3 px-5 py-3 hover:bg-teal-50 transition-colors cursor-pointer text-left"
                    >
                      <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
                        <img src={s.image} alt={s.nom} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{s.nom}</div>
                        <div className="text-xs text-teal-600">{s.glucidesPour100g}g G/100g · IG {s.indexGlycemique || 'N/A'}</div>
                      </div>
                      {s.aliases && s.aliases.length > 0 && (
                        <div className="hidden sm:flex items-center gap-1">
                          {s.aliases.slice(0, 2).map((a) => (
                            <span key={a} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{a}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setActiveCategory('tous')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeCategory === 'tous' ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-teal-300'}`}
            >
              Tous ({aliments.length})
            </button>
            {categoriesAliments.map((cat) => {
              const count = aliments.filter((a) => a.categorie === cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat.id ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-teal-300'}`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={cat.icon}></i>
                  </div>
                  {cat.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Results count */}
          <p className="text-gray-500 text-sm mb-5">
            {loading ? 'Chargement des aliments...' : `${filtered.length} aliment${filtered.length > 1 ? 's' : ''} trouvé${filtered.length > 1 ? 's' : ''}`}
          </p>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((aliment) => (
              <AlimentCard key={aliment.id} aliment={aliment} onClick={() => setSelectedAliment(aliment)} />
            ))}
          </div>

          {filtered.length === 0 && !loading && (
            <div className="text-center py-20 text-gray-400">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-5xl"></i>
              </div>
              <p className="text-lg">Aucun aliment trouvé</p>
              <p className="text-sm mt-1">Essayez un autre terme ou synonyme</p>
            </div>
          )}
        </div>
      </div>

      {selectedAliment && (
        <AlimentDetail aliment={selectedAliment} onClose={() => setSelectedAliment(null)} />
      )}
    </>
  );
}